import { memo, useMemo } from 'react';
import { Group, Select } from '@mantine/core';
import type { ComboboxItem } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { getBasemapById } from '@/config/maplibre';
import type { BasemapId } from '@/config/maplibre';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';

import styles from './BasemapSelect.module.css';

/** Width reserved for the basemap thumbnail preview in the Select left section. */
const SELECT_THUMBNAIL_SECTION_WIDTH = 52;

/** Select option carrying its basemap thumbnail so it is resolved only once. */
interface BasemapOption extends ComboboxItem {
  thumbnail?: string;
}

interface BasemapSelectProps {
  /** Selectable basemap ids, in canonical display order. */
  options: BasemapId[];
  /** Currently selected basemap. */
  value: BasemapId;
  /** Called when the selection changes. */
  onChange: (id: BasemapId) => void;
  /** Accessible label — the surrounding section provides its own visible heading. */
  label: string;
}

/**
 * Dropdown for picking a single basemap, rendering a thumbnail next to the
 * selected value and inside every option. Shared between the workspace map
 * config and the share form so the control stays identical in both places.
 */
export const BasemapSelect = memo(function BasemapSelect({
  options,
  value,
  onChange,
  label,
}: BasemapSelectProps) {
  const { t } = useTranslation();

  const data = useMemo<BasemapOption[]>(
    () =>
      options.flatMap((id) => {
        const basemap = getBasemapById(id);
        return basemap
          ? [
              {
                value: id,
                label: t(basemap.labelKey),
                thumbnail: basemap.thumbnail,
              },
            ]
          : [];
      }),
    [options, t]
  );

  const selectedThumbnail = getBasemapById(value)?.thumbnail;

  return (
    <Select
      data={data}
      value={value}
      onChange={(next) => next && onChange(next as BasemapId)}
      allowDeselect={false}
      aria-label={label}
      comboboxProps={{
        withinPortal: true,
        zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
      }}
      leftSectionWidth={SELECT_THUMBNAIL_SECTION_WIDTH}
      leftSection={
        selectedThumbnail ? (
          <img src={selectedThumbnail} alt="" className={styles.optionThumb} />
        ) : undefined
      }
      renderOption={({ option }) => {
        const { thumbnail } = option as BasemapOption;
        return (
          <Group gap="xs" wrap="nowrap">
            {thumbnail && (
              <img src={thumbnail} alt="" className={styles.optionThumb} />
            )}
            <span>{option.label}</span>
          </Group>
        );
      }}
    />
  );
});
